// Card 02, soundings (R2616; ui-map-interface.md §17.3.02,
// v2-the-deep.md §13; mockup round 028 card 02 and round 088 card 04).
//
// Every chart since the 1600s scatters depth figures over the water. This
// piece writes a figure in metres on about one water cell in `every`, at
// close zoom only: upright mono figures in the modern reading, the italic
// Fell figures in the historical one.
//
// **THE FIGURE READS THE BAND.** The hello carries the depth band of each
// cell while the flag `depth` is latched (`Hello.depth`, R2609), and no
// depth in metres. A figure is chart text (R2565): the top of the band of
// the cell plus its span, spread over the first `ramp_cells` hex steps in
// from the shallower edge of the band, so the figures grow toward the
// middle of a band. `soundingMetres` gives the figure of every cell, and
// the isobaths (card 13) and the legend read the same answer, so the chart
// tells one depth story (§13).
//
// **IT DRAWS WHAT THE WIRE SENDS.** A world with no band field draws no
// figure, and a cell with no band (rock, or water the survey does not
// hold) draws none. The piece reads no flag (§14.1 rule 5).
//
// **A WINDOW, NOT THE WORLD.** The sea pack repacks whole when any of its
// static pieces asks, and the imprint asks once a world minute. So the
// piece writes the figures of the view grown by a slack alone, and a pan
// past the slack is its own edge (`outgrown`), as the wrecks do (R801).

import { CHART } from '../../../gen/presentation';
import { BandStepsPatch, bandDepthSteps, fieldCellsSince, fieldRev } from '../../../deep';
import { COLS, ROWS, cellCenterX, cellCenterY } from '../../../world';
import type { ViewRect } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf, inkOf, leftRect, slackRect } from './shared';

/** The figures of the piece's own table. */
const T = CHART.pieces.soundings;

/** The share of the view the pack window grows by on each side. */
const SLACK = 0.5;

/** The last field `soundingMetres` read, and its answer. */
let cachedFor: Uint8Array | null = null;
let cachedGrid = '';
let cachedRev = -1;
let cached: Uint16Array = new Uint16Array(0);
/** The steps the cached answer was read from, and the patch of a pass. */
let cachedSteps: Uint8Array = new Uint8Array(0);
const stepsPatch = new BandStepsPatch();

/**
 * The sounding of every cell in metres, 0 for a cell with no band. It is
 * computed once for each band field and kept, so every reader of one
 * hello shares it. A survey pass updates the cells round the cells it
 * wrote in place (R2897), and a full frame computes it whole.
 */
export function soundingMetres(depth: Uint8Array): Uint16Array {
  const grid = `${COLS}x${ROWS}`;
  // A survey pass writes the bands of its new cells into the same field,
  // so the cache keys on the revision too (R2626).
  const rev = fieldRev();
  if (depth === cachedFor && grid === cachedGrid && cachedRev === rev) return cached;
  const ramp = Math.max(1, Math.round(T.fig.ramp_cells));
  if (depth === cachedFor && grid === cachedGrid) {
    stepsPatch.begin(depth, cachedSteps, ramp + 1);
    if (fieldCellsSince(cachedRev, stepsPatch.visit)) {
      stepsPatch.finish();
      for (let i = 0; i < stepsPatch.count; i++) {
        const c = stepsPatch.list[i], b = depth[c];
        cached[c] = b === 0 ? 0 : bandMetres(b, cachedSteps[c], ramp);
      }
      cachedRev = rev;
      return cached;
    }
  }
  const steps = bandDepthSteps(depth, ramp + 1);
  cachedSteps = steps;
  const out = new Uint16Array(depth.length);
  for (let c = 0; c < depth.length; c++) {
    const b = depth[c];
    if (b === 0) continue;
    out[c] = bandMetres(b, steps[c], ramp);
  }
  cachedFor = depth;
  cachedGrid = grid;
  cachedRev = rev;
  cached = out;
  return out;
}

/** The sounding of one band `stepsIn` hex steps in from its shallower
 *  edge, rounded to `round_m`. */
export function bandMetres(band: number, stepsIn: number, ramp: number = T.fig.ramp_cells): number {
  const top = T.arr.band_top_m[band - 1] ?? 0;
  const span = T.arr.band_span_m[band - 1] ?? 0;
  const k = Math.min(1, Math.max(0, stepsIn - 1) / Math.max(1, ramp));
  const step = Math.max(1, T.fig.round_m);
  return Math.round((top + span * k) / step) * step;
}

/** The figure of the shallow end of a band in metres: the legend writes it
 *  under the swatch of the band. */
export function bandTopMetres(band: number): number {
  return T.arr.band_top_m[band - 1] ?? 0;
}

/** Does this cell carry a figure: one cell in `every`, by a hash. */
function picked(cell: number, every: number): boolean {
  return (Math.imul(cell + 1, 2654435761) >>> 0) % every === 0;
}

/** One figure the last pack wrote. */
interface Figure { x: number; y: number; text: string }

export class SoundingsPiece implements Piece {
  readonly id = 'soundings' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The sea the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'sites', 'reading', 'zoomband', 'font', 'survey'] as const;

  /** The figures of the last pack. The tests read them. */
  figures: Figure[] = [];
  /** The world rect the last pack covers. */
  private win: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };

  visible(ctx: ChartContext): boolean {
    return ctx.state.depthBands !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.figures = [];
    const depth = ctx.state.depthBands;
    const win = slackRect(ctx.view, SLACK);
    this.win = win;
    if (!depth) return;
    const metres = soundingMetres(depth);
    const every = Math.max(1, Math.round(T.fig.every));
    const clear = T.fig.site_clear_px;
    const sites: { x: number; y: number }[] = [];
    for (const s of ctx.state.sites.values()) sites.push({ x: cellCenterX(s.cell), y: cellCenterY(s.cell) });
    for (let c = 0; c < metres.length; c++) {
      const m = metres[c];
      if (m === 0 || !picked(c, every)) continue;
      const x = cellCenterX(c), y = cellCenterY(c);
      if (x < win.x0 || x > win.x1 || y < win.y0 || y > win.y1) continue;
      if (sites.some((s) => Math.hypot(s.x - x, s.y - y) < clear)) continue;
      this.figures.push({ x, y, text: String(m) });
    }
  }

  /** A pan past the slack is an edge no reason names: one compare. */
  outgrown(ctx: ChartContext): boolean {
    return leftRect(ctx.view, this.win);
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const r = T[ctx.reading];
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    for (const f of this.figures) {
      ctx.glyphs.word(sinks.quads, f.text, f.x, f.y, face, T.text.main, ink, r.alpha,
        { anchor: 'centre' });
    }
  }
}

/** The one instance the registry holds. */
export const soundings = new SoundingsPiece();
